import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';
import axios from 'axios';
import { endpoints } from '../../../api/api';
import {
  GrammarError,
  extractTextWithPositions,
  simpleHash,
} from '../extensions/GrammarCheck';
import { trackSpellcheckFailed } from '../../../utils/analytics';

interface UseGrammarCheckOptions {
  editor: Editor | null;
  language: string;
  activeSectionId: string;
  debounceMs?: number;
  enabled?: boolean;
}

export const useGrammarCheck = ({
  editor,
  language,
  activeSectionId,
  debounceMs = 1200,
  enabled = true,
}: UseGrammarCheckOptions) => {
  const [isChecking, setIsChecking] = useState(false);
  const [errors, setErrors] = useState<GrammarError[]>([]);
  const [activeError, setActiveError] = useState<GrammarError | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<Map<string, GrammarError[]>>(new Map());
  const lastSectionRef = useRef<string>(activeSectionId);

  // Clear errors when section changes
  useEffect(() => {
    if (lastSectionRef.current !== activeSectionId) {
      lastSectionRef.current = activeSectionId;
      cacheRef.current.clear();
      setErrors([]);
      setActiveError(null);
      if (editor) {
        editor.commands.clearGrammarErrors();
      }
    }
  }, [activeSectionId, editor]);

  // Core check function
  const performCheck = useCallback(async () => {
    if (!editor || editor.isDestroyed || !enabled) return;

    const { text, posMap } = extractTextWithPositions(editor.state.doc);

    if (text.trim().length < 10) {
      editor.commands.clearGrammarErrors();
      setErrors([]);
      return;
    }

    const hash = simpleHash(text);

    // Check cache
    const cached = cacheRef.current.get(hash);
    if (cached) {
      editor.commands.setGrammarErrors(cached);
      setErrors(cached);
      return;
    }

    setIsChecking(true);
    editor.commands.setGrammarChecking(true);

    try {
      const response = await axios.post(endpoints.grammarCheck, {
        text,
        language,
      });

      if (!response.data.success) {
        console.warn('Grammar check failed:', response.data.message);
        return;
      }

      const grammarErrors: GrammarError[] = response.data.matches
        .map((match: any): GrammarError | null => {
          const from = posMap[match.offset];
          const to = posMap[match.offset + match.length - 1];

          if (from === undefined || to === undefined) return null;

          return {
            id: `${match.offset}-${match.length}-${match.rule.id}`,
            offset: match.offset,
            length: match.length,
            from,
            to: to + 1, // ProseMirror 'to' is exclusive
            message: match.message,
            shortMessage: match.shortMessage,
            replacements: match.replacements,
            rule: match.rule,
            dismissed: false,
          };
        })
        .filter(Boolean) as GrammarError[];

      // Cache (bounded at 20 entries)
      cacheRef.current.set(hash, grammarErrors);
      if (cacheRef.current.size > 20) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey) cacheRef.current.delete(firstKey);
      }

      editor.commands.setGrammarErrors(grammarErrors);
      setErrors(grammarErrors);

    } catch (error: any) {
      console.error('Grammar check request failed:', error);
      trackSpellcheckFailed(error?.response?.data?.message || error?.message || 'unknown');
    } finally {
      setIsChecking(false);
      if (editor && !editor.isDestroyed) {
        editor.commands.setGrammarChecking(false);
      }
    }
  }, [editor, language, enabled]);

  // Manual trigger
  const triggerCheck = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    performCheck();
  }, [performCheck]);

  // Clear errors
  const clearErrors = useCallback(() => {
    if (editor) {
      editor.commands.clearGrammarErrors();
    }
    setErrors([]);
    setActiveError(null);
  }, [editor]);

  // Dismiss a single error
  const dismissError = useCallback((errorId: string) => {
    if (editor) {
      editor.commands.dismissGrammarError(errorId);
    }
    setErrors(prev => prev.filter(e => e.id !== errorId));
    setActiveError(null);
  }, [editor]);

  // Apply a suggestion
  const applySuggestion = useCallback((errorId: string, replacement: string) => {
    if (editor) {
      editor.commands.applyGrammarSuggestion(errorId, replacement);
    }
    setErrors(prev => prev.filter(e => e.id !== errorId));
    setActiveError(null);
  }, [editor]);

  // Auto-check on content update (debounced)
  useEffect(() => {
    if (!editor || !enabled) return;

    const handleUpdate = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        performCheck();
      }, debounceMs);
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [editor, enabled, debounceMs, performCheck]);

  return {
    errorCount: errors.length,
    isChecking,
    triggerCheck,
    clearErrors,
    errors,
    activeError,
    setActiveError,
    dismissError,
    applySuggestion,
  };
};
